import type { GeoPoint } from '@citizen-walk/shared-types';

/**
 * 楽観 (optimistic) pending 投稿の同一タブ用ストア (docs/handoff_2026-06-03 根本対処)。
 *
 * ## なぜ必要か (真因)
 * Android 機内モードでは `navigator.onLine` が `true` のまま残るため、`createPost` は
 * online の `setDoc` 経路に入り、投稿は Firestore オフラインキャッシュにキューされる
 * (`hasPendingWrites=true`)。ところが `firebase.ts` の `disableNetwork` は `offline`
 * イベントでしか呼ばれず、機内モードでは発火しない。結果 SDK は「オンラインのまま
 * サーバ到達待ち」状態になり、**新しく張った onSnapshot リスナーがキャッシュ上の
 * pending doc を即座に配信しない** (Firestore SDK の既知挙動 = `firebase.ts` の
 * 「ネットワーク復帰待ち」コメント参照)。そのため `usePosts` 経由の path B
 * (`posts.filter(p => p.pendingWrite)`) は**オフライン中は発火できず**、地図に pending
 * ピンが出ない。機内モード解除でサーバ応答が来た瞬間にだけキャッシュ doc が配信され、
 * 一瞬 pending ピン → 通常ピンに変わる、という観察と一致する。
 *
 * ## このストアの役割
 * 投稿を送信した瞬間に、ネットワーク状態 / リスナー配信 / `setDoc` の解決に**一切依存せず**
 * pending ピンを地図へ即時表示するための「第3の表示ソース (path C)」。Firestore リスナーが
 * 同 doc を配信したら id 一致で dedup され、二重ピンにはならない (MapPage 側で実施)。
 *
 * - tab 横断は不要 (投稿したタブで即表示できれば足りる。他タブは同期後に Firestore 経由で出る)。
 *   そのため outbox の BroadcastChannel と違い**同一タブの listener のみ**で軽量実装する。
 * - `thumbBlob` はローカル生成サムネ。MapView 側で object URL 化してポップアップに使う
 *   (URL の生成/revoke は MapView が管理するため、このストアは Blob を保持するだけ)。
 */
export interface OptimisticPost {
  /** 送信先 Firestore doc の ID (createPost が pre-allocate)。dedup キーに使う。 */
  id: string;
  /** 投稿先 districtId (地図は現在地区のみ表示するため filter に使う)。 */
  districtId: string;
  title: string;
  location: GeoPoint;
  /** ローカル生成サムネ (写真なしは null)。 */
  thumbBlob: Blob | null;
}

const store = new Map<string, OptimisticPost>();
const listeners = new Set<() => void>();

function notify(): void {
  for (const cb of [...listeners]) {
    try {
      cb();
    } catch {
      /* 1 つの listener 失敗が他に波及しないよう握りつぶす (outbox.notifyOutboxChanged 同方針) */
    }
  }
}

/** 楽観 pending 投稿を登録 (createPost が送信前に呼ぶ)。同 id は上書き。 */
export function addOptimisticPost(post: OptimisticPost): void {
  store.set(post.id, post);
  notify();
}

/**
 * 楽観 pending 投稿を除去。
 *   - Firestore リスナーが同 doc を配信したら MapPage の cleanup effect が呼ぶ (役目終了)。
 *   - `setDoc` が hard error (rules 拒否 / validation) のとき createPost が呼ぶ (ghost 防止)。
 */
export function removeOptimisticPost(id: string): void {
  if (store.delete(id)) notify();
}

/** 現在の楽観 pending 投稿一覧 (登録順)。 */
export function listOptimisticPosts(): OptimisticPost[] {
  return [...store.values()];
}

/** 同一タブの購読 (useOptimisticPosts が使用、unmount 時 unsubscribe)。 */
export function subscribeOptimisticPosts(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

/** test 用 internal reset。 */
export function __resetOptimisticPosts(): void {
  store.clear();
  listeners.clear();
}
